import { Injectable, isDevMode } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { Store } from '@ngxs/store';
import { DateTime } from 'luxon';
import { BehaviorSubject, Observable } from 'rxjs';
import { parseMentionWire } from 'src/app/components/mention-composer/mention-composer.util';
import { AuthState } from 'src/app/store/auth/auth.state';
import { GrowthBookService } from 'src/app/services/growthbook/service';
import { SegmentService } from 'src/app/services/segment/segment.service';
import { McpApiKeyService } from 'src/app/services/mcp/mcp-api-key.service';
import { AgentGatewayService } from './agent-gateway.service';
import { CopilotContextBuilder } from './copilot-context';
import { CopilotMcpConfigService } from './copilot-mcp-config.service';
import { COPILOT_FEATURE_FLAG, COPILOT_MAX_CONVERSATIONS, COPILOT_MCP_DISCLOSURE_KEY } from './copilot.constants';
import {
  COPILOT_UNTITLED_CONVERSATION,
  clearCopilotConversationsStore,
  createEmptyConversation,
  deriveConversationTitle,
  loadCopilotConversationsStore,
  saveCopilotConversationsStore
} from './copilot-conversations.util';
import { loadCopilotAgentSettings, saveCopilotAgentSettings, tempoPersonalityForAdvisor } from './copilot-agent-settings.util';
import {
  defaultLast7DaysRange,
  normalizeInclusivePanelDateRange,
  panelScopeHasDateRange
} from './copilot-scope.util';
import { mapDateClarificationChip } from './copilot-date-clarification.util';
import { extractInlineTablesFromAnswer } from './extract-answer-tables.util';
import { appendToolStepFromStatus, markToolStepsDone } from './copilot-tool-steps.util';
import {
  AgentGatewayErrorKind,
  AgentGatewayContext,
  ConversationScope,
  CopilotAction,
  CopilotAgentSettings,
  CopilotClarification,
  CopilotConversationTab,
  CopilotLiveDataStatus,
  CopilotPanelScope,
  CopilotSendOptions,
  CopilotUiMessage,
  GATEWAY_CHIP_OPEN_SCOPE,
  GATEWAY_CHIP_RETRY_LAST,
  HelpLink,
  NavAction,
  AdvisorPersonaId,
  TempoPersonalityId
} from './copilot.types';
import type { ProductivityInsight } from '../insights/insights.types';
import { environment } from 'src/environments/environment';

/** Location tag attached to copilot analytics events. */
const COPILOT_EVENT_LOCATION = 'Copilot Side Panel';

/** Name attached to the auto-provisioned MCP key so users can identify it in Integrations. */
const COPILOT_MCP_KEY_NAME = 'Tempo in-app assistant';

@Injectable({ providedIn: 'root' })
export class CopilotFacadeService {
  private readonly isOpen = new BehaviorSubject<boolean>(false);
  private readonly isSending = new BehaviorSubject<boolean>(false);
  private readonly messages = new BehaviorSubject<CopilotUiMessage[]>([]);
  private readonly liveData = new BehaviorSubject<CopilotLiveDataStatus>('connected');
  private readonly liveDataDisclosurePending = new BehaviorSubject<boolean>(false);
  private readonly conversationsState = new BehaviorSubject<CopilotConversationTab[]>([]);
  private readonly activeConversationIdState = new BehaviorSubject<string>('');

  readonly isOpen$: Observable<boolean> = this.isOpen.asObservable();
  readonly isSending$: Observable<boolean> = this.isSending.asObservable();
  readonly messages$: Observable<CopilotUiMessage[]> = this.messages.asObservable();
  readonly liveData$: Observable<CopilotLiveDataStatus> = this.liveData.asObservable();
  readonly liveDataDisclosurePending$: Observable<boolean> =
    this.liveDataDisclosurePending.asObservable();
  readonly conversations$: Observable<CopilotConversationTab[]> =
    this.conversationsState.asObservable();
  readonly activeConversationId$: Observable<string> =
    this.activeConversationIdState.asObservable();

  private readonly panelScopeState = new BehaviorSubject<CopilotPanelScope>({});
  readonly panelScope$ = this.panelScopeState.asObservable();

  /** Kevin-style daily brief greeting for the empty state (null = use static copy). */
  private readonly briefGreetingState = new BehaviorSubject<string | null>(null);
  readonly briefGreeting$ = this.briefGreetingState.asObservable();

  private readonly agentSettingsState = new BehaviorSubject<CopilotAgentSettings>(
    loadCopilotAgentSettings(),
  );
  readonly agentSettings$ = this.agentSettingsState.asObservable();

  private readonly activeClarificationState =
    new BehaviorSubject<CopilotClarification | null>(null);
  readonly activeClarification$ = this.activeClarificationState.asObservable();

  private lastCombinedId: string | null = null;
  /** Single-flight guard so concurrent turns don't each mint an MCP key. */
  private keyProvisioning: Promise<void> | null = null;
  private panelScope: CopilotPanelScope = {};
  private persistTimer: ReturnType<typeof setTimeout> | null = null;
  private pendingResend: (CopilotSendOptions & {
    displayText: string;
    gatewayMessage: string;
  }) | null = null;
  private pendingComposerWire: string | null = null;
  private lastSendPayload: (CopilotSendOptions & {
    displayText: string;
    gatewayMessage: string;
  }) | null = null;

  constructor(
    private readonly agentGateway: AgentGatewayService,
    private readonly context: CopilotContextBuilder,
    private readonly growthBook: GrowthBookService,
    private readonly segment: SegmentService,
    private readonly store: Store,
    private readonly mcpConfig: CopilotMcpConfigService,
    private readonly mcpApiKeys: McpApiKeyService,
    private readonly translate: TranslateService,
  ) {
    this.syncLiveDataStatusFromStorage();
    this.hydrateConversations();
    this.lastCombinedId = this.store.selectSnapshot(AuthState.combinedId);

    // Reset conversation whenever the user/company identity changes.
    this.store.select(AuthState.combinedId).subscribe((combinedId) => {
      if (combinedId !== this.lastCombinedId) {
        this.lastCombinedId = combinedId;
        this.clearAllConversations();
        this.isOpen.next(false);
        document.body.classList.remove('copilot-open');
        this.mcpConfig.clear();
        this.liveDataDisclosurePending.next(false);
        this.syncLiveDataStatusFromStorage();
      }
    });
  }

  private syncLiveDataStatusFromStorage(): void {
    this.liveData.next('connected');
  }

  private hasLiveDataDisclosureAck(): boolean {
    try {
      return localStorage.getItem(COPILOT_MCP_DISCLOSURE_KEY) === '1';
    } catch {
      return false;
    }
  }

  private ackLiveDataDisclosure(): void {
    try {
      localStorage.setItem(COPILOT_MCP_DISCLOSURE_KEY, '1');
    } catch {
      // Ignore storage failures.
    }
    this.liveDataDisclosurePending.next(false);
  }

  /** User confirmed the first-run live-data disclosure — provision MCP key. */
  async confirmLiveDataDisclosure(): Promise<void> {
    this.ackLiveDataDisclosure();
    this.pushAssistantInfo(this.translate.instant('copilot.liveData.disclosureAccepted'));
    await this.ensureLiveDataKey();
  }

  /** Declined live-data — help-only mode until the user connects manually. */
  declineLiveDataDisclosure(): void {
    this.ackLiveDataDisclosure();
    this.liveData.next('disconnected');
    this.pushAssistantInfo(this.translate.instant('copilot.liveData.disclosureDeclined'));
  }

  get isEnabled(): boolean {
    return this.agentGateway.isConfigured
      && this.growthBook.getFeatureValue<boolean>(COPILOT_FEATURE_FLAG, false) === true;
  }

  get liveDataStatus(): CopilotLiveDataStatus {
    return this.liveData.value;
  }

  /**
   * Connect Tempo to live Time Doctor workspace data by minting a personal MCP key and storing it
   * client-side. Subsequent messages send the key so the agent can run live workspace tools.
   */
  async connectLiveData(): Promise<void> {
    if (this.liveData.value !== 'disconnected') {
      return;
    }

    const company = this.store.selectSnapshot(AuthState.company);
    const user = this.store.selectSnapshot(AuthState.user);
    if (!company?.id || !user?.id) {
      this.pushAssistantInfo(this.translate.instant('copilot.liveData.connectError'), true);
      return;
    }

    this.liveData.next('connecting');
    this.ackLiveDataDisclosure();
    try {
      const result = await this.mcpApiKeys.createMyKey(company.id, user.id, COPILOT_MCP_KEY_NAME);
      const credential = result?.credential;
      if (!credential) {
        throw new Error('No credential returned');
      }
      this.mcpConfig.setApiKey(credential, result?.key?.id);
      this.liveData.next('connected');
      this.ensureDefaultDateRangeForLiveData();
      this.segment.trackInAppEvents('Copilot', {
        name: 'Live Data Connected',
        location: 'Copilot Side Panel',
      });
      this.pushAssistantInfo(this.translate.instant('copilot.liveData.connected'));
    } catch {
      this.liveData.next('disconnected');
      this.pushAssistantInfo(this.translate.instant('copilot.liveData.connectError'), true);
    }
  }

  disconnectLiveData(): void {
    void this.revokeStoredMcpKey();
    this.mcpConfig.clear();
    this.liveData.next('disconnected');
    this.segment.trackInAppEvents('Copilot', {
      name: 'Live Data Disconnected',
      location: 'Copilot Side Panel',
    });
    this.pushAssistantInfo(this.translate.instant('copilot.liveData.disconnected'));
  }

  private async revokeStoredMcpKey(): Promise<void> {
    const keyId = this.mcpConfig.getKeyId();
    if (!keyId) {
      return;
    }
    const company = this.store.selectSnapshot(AuthState.company);
    const user = this.store.selectSnapshot(AuthState.user);
    if (!company?.id || !user?.id) {
      return;
    }
    try {
      await this.mcpApiKeys.revokeMyKey(company.id, user.id, keyId);
    } catch {
      // Best-effort revoke; client state is cleared regardless.
    }
  }

  open(): void {
    this.isOpen.next(true);
    document.body.classList.add('copilot-open');
    this.closeHubspotWidgetIfOpen();
    this.ensurePanelTimezone();
    this.syncPanelScopeFromRoute();
    // Seed a default range even when live-data MCP is not connected yet.
    this.ensureDefaultDateRangeForBrief();
    if (this.mcpConfig.isConnected()) {
      this.liveDataDisclosurePending.next(false);
      this.ensureDefaultDateRangeForLiveData();
    } else {
      // Skip the first-run disclosure card; silently provision MCP for live data.
      this.liveDataDisclosurePending.next(false);
      void this.ensureLiveDataKey();
    }
    this.segment.trackInAppEvents('Copilot', {
      name: 'Copilot Opened',
      location: 'Header',
    });
  }

  /**
   * Ensure a personal MCP key exists before gateway calls that need live stats
   * (e.g. GET /insights/feed). Same silent auto-provision as opening Copilot.
   */
  ensureLiveDataKeyForGateway(): Promise<void> {
    return this.ensureLiveDataKey();
  }

  /**
   * Ensure a personal MCP key exists so the gateway can read the caller's LIVE
   * workspace data on their behalf. Auto-provisions silently the first time per
   * browser + identity — no "Connect live workspace data" click and no chat
   * notice. Single-flight and a no-op once a key is stored; failures are swallowed
   * (help and other answers still work without live data).
   */
  private ensureLiveDataKey(): Promise<void> {
    if (this.mcpConfig.getApiKey()) {
      return Promise.resolve();
    }
    if (this.keyProvisioning) {
      return this.keyProvisioning;
    }

    const company = this.store.selectSnapshot(AuthState.company);
    const user = this.store.selectSnapshot(AuthState.user);
    if (!company?.id || !user?.id) {
      return Promise.resolve();
    }

    this.liveData.next('connecting');
    this.keyProvisioning = this.mcpApiKeys
      .createMyKey(company.id, user.id, COPILOT_MCP_KEY_NAME)
      .then((result) => {
        const credential = result?.credential;
        if (credential) {
          this.mcpConfig.setApiKey(credential, result?.key?.id);
        }
        this.liveData.next('connected');
        this.ensureDefaultDateRangeForLiveData();
      })
      .catch(() => {
        // Provisioning failed (e.g. role/plan). Live data may be unavailable, but
        // help and other answers still work — surface "connected" optimistically
        // rather than nagging; the gateway degrades gracefully.
        this.liveData.next('connected');
      })
      .finally(() => {
        this.keyProvisioning = null;
      });
    return this.keyProvisioning;
  }

  close(): void {
    this.isOpen.next(false);
    document.body.classList.remove('copilot-open');
  }

  /** Collapse HubSpot messenger if it was expanded so it does not fight Copilot. */
  private closeHubspotWidgetIfOpen(): void {
    try {
      window.HubSpotConversations?.widget?.close?.();
    } catch {
      // HubSpot may be unloaded or blocked; ignore.
    }
  }

  toggle(): void {
    if (this.isOpen.value) {
      this.close();
    } else {
      this.open();
    }
  }

  /**
   * Open Tempo with an insight card's context and seeded question pre-loaded.
   */
  async analyzeInsight(insight: ProductivityInsight): Promise<void> {
    if (!this.isEnabled) {
      return;
    }
    this.open();
    if (insight.period?.from && insight.period?.to) {
      this.setPanelDateRange(insight.period.from, insight.period.to, false);
    }

    const selection =
      insight.subject.kind === 'user'
        ? { selectedUsers: [{ id: insight.subject.id, name: insight.subject.name }] }
        : insight.subject.kind === 'team'
          ? { selectedGroups: [{ id: insight.subject.id, name: insight.subject.name }] }
          : undefined;

    const prompt = insight.suggestedCopilotPrompt;
    // Prefer panelScope (already normalized by setPanelDateRange) over raw
    // insight.period — exclusive ISO / future ends would otherwise override the
    // inclusive picker range on the gateway wire (STAFF-145650).
    await this.send(prompt, {
      displayText: prompt,
      gatewayMessage: prompt,
      selection,
      contextOverrides: {
        route: 'insights',
        filters: {
          insightId: insight.id,
          insightType: insight.type,
        },
      },
    });
  }

  resetThread(): void {
    this.createConversation();
  }

  /** Start a new empty chat tab. Reuses the active blank tab; otherwise always creates one. */
  createConversation(): void {
    if (this.isSending.value) {
      return;
    }
    this.flushPersist();

    const active = this.activeConversation();
    const activeIsBlank = !!active && this.isBlankConversation(active);
    if (activeIsBlank) {
      // Already on a fresh chat — still clear local UI state and keep focus path working.
      this.setActiveMessages([], { persist: true });
      this.clearClarificationState();
      return;
    }

    let conversations = this.conversationsState.value.filter(
      (c) => !this.isBlankConversation(c),
    );
    while (conversations.length >= COPILOT_MAX_CONVERSATIONS) {
      const oldest = conversations.reduce((a, b) =>
        a.updatedAt <= b.updatedAt ? a : b,
      );
      conversations = conversations.filter((c) => c.id !== oldest.id);
    }

    const next = createEmptyConversation();
    conversations = [...conversations, next];
    this.conversationsState.next(conversations);
    this.activeConversationIdState.next(next.id);
    this.setActiveMessages([], { persist: true });
    this.clearClarificationState();
    this.isSending.next(false);
  }

  private isBlankConversation(tab: CopilotConversationTab): boolean {
    const hasUserContent = tab.messages.some(
      (m) => m.role === 'user' && !m.transient && !!m.content?.trim(),
    );
    return !hasUserContent && !tab.conversationId;
  }

  switchConversation(id: string): void {
    if (this.isSending.value || id === this.activeConversationIdState.value) {
      return;
    }
    const target = this.conversationsState.value.find((c) => c.id === id);
    if (!target) {
      return;
    }
    this.flushPersist();
    this.activeConversationIdState.next(id);
    this.messages.next([...target.messages]);
    this.clearClarificationState();
    this.briefGreetingState.next(null);
  }

  closeConversation(id: string): void {
    if (this.isSending.value) {
      return;
    }
    const conversations = this.conversationsState.value;
    const index = conversations.findIndex((c) => c.id === id);
    if (index < 0) {
      return;
    }

    const remaining = conversations.filter((c) => c.id !== id);
    if (remaining.length === 0) {
      const fresh = createEmptyConversation();
      this.conversationsState.next([fresh]);
      this.activeConversationIdState.next(fresh.id);
      this.setActiveMessages([], { persist: true });
      this.clearClarificationState();
      return;
    }

    const wasActive = id === this.activeConversationIdState.value;
    this.conversationsState.next(remaining);
    if (wasActive) {
      const nextIndex = Math.min(index, remaining.length - 1);
      const next = remaining[nextIndex];
      this.activeConversationIdState.next(next.id);
      this.messages.next([...next.messages]);
      this.clearClarificationState();
      this.briefGreetingState.next(null);
    }
    this.schedulePersist();
  }

  private clearAllConversations(): void {
    if (this.persistTimer) {
      clearTimeout(this.persistTimer);
      this.persistTimer = null;
    }
    clearCopilotConversationsStore();
    const fresh = createEmptyConversation();
    this.conversationsState.next([fresh]);
    this.activeConversationIdState.next(fresh.id);
    this.messages.next([]);
    this.briefGreetingState.next(null);
    this.isSending.next(false);
    this.clearClarificationState();
    this.flushPersist();
  }

  private hydrateConversations(): void {
    const store = loadCopilotConversationsStore();
    this.conversationsState.next(store.conversations);
    this.activeConversationIdState.next(store.activeId);
    const active = store.conversations.find((c) => c.id === store.activeId);
    this.messages.next([...(active?.messages ?? [])]);
  }

  private activeConversation(): CopilotConversationTab | undefined {
    const id = this.activeConversationIdState.value;
    return this.conversationsState.value.find((c) => c.id === id);
  }

  private activeGatewayConversationId(): string | undefined {
    return this.activeConversation()?.conversationId;
  }

  private setActiveMessages(
    msgs: CopilotUiMessage[],
    options?: { persist?: boolean },
  ): void {
    this.messages.next(msgs);
    const activeId = this.activeConversationIdState.value;
    const derived = deriveConversationTitle(msgs);
    this.conversationsState.next(
      this.conversationsState.value.map((c) => {
        if (c.id !== activeId) {
          return c;
        }
        // Keep an established title; only replace when blank or first real user turn lands.
        const keepTitle =
          c.title
          && c.title !== COPILOT_UNTITLED_CONVERSATION
          && derived === COPILOT_UNTITLED_CONVERSATION;
        return {
          ...c,
          messages: msgs,
          title: keepTitle ? c.title : derived,
          updatedAt: new Date().toISOString(),
        };
      }),
    );
    if (options?.persist === false) {
      return;
    }
    this.schedulePersist();
  }

  private schedulePersist(): void {
    if (this.persistTimer) {
      clearTimeout(this.persistTimer);
    }
    this.persistTimer = setTimeout(() => {
      this.persistTimer = null;
      this.flushPersist();
    }, 250);
  }

  private flushPersist(): void {
    if (this.persistTimer) {
      clearTimeout(this.persistTimer);
      this.persistTimer = null;
    }
    saveCopilotConversationsStore({
      activeId: this.activeConversationIdState.value,
      conversations: this.conversationsState.value,
    });
  }

  /** Seed the in-panel date range from the current report URL, when present. */
  syncPanelScopeFromRoute(): void {
    const ctx = this.context.buildAgentGatewayContext(undefined, this.panelScope);
    if (ctx.dateRange?.from && ctx.dateRange?.to) {
      this.panelScope = {
        ...this.panelScope,
        dateRange: { from: ctx.dateRange.from, to: ctx.dateRange.to },
      };
      this.panelScopeState.next({ ...this.panelScope });
    }
  }

  setPanelDateRange(from: string, to: string, autoResend = true): void {
    if (!from || !to) {
      return;
    }
    // Clamp with viewer "today" (same as sidepanel maxSelectableDate). Using only
    // panelScope.timezone / UTC can treat an inclusive end on the viewer's today as
    // exclusive and shrink the range by a day when zones disagree.
    const viewerTz = this.store.selectSnapshot(AuthState.userTimezone)?.trim() || undefined;
    const timeZone = this.panelScope.timezone?.trim() || viewerTz || undefined;
    const todayZone = viewerTz || timeZone || 'utc';
    const todayDt = DateTime.now().setZone(todayZone);
    const today = (todayDt.isValid ? todayDt : DateTime.utc()).toISODate() ?? undefined;
    const normalized =
      normalizeInclusivePanelDateRange(from, to, {
        timeZone,
        today,
      }) ?? { from, to };
    const current = this.panelScope.dateRange;
    if (current?.from === normalized.from && current?.to === normalized.to) {
      if (autoResend && this.activeClarificationState.value === 'date_range') {
        void this.resendPending();
      }
      return;
    }
    this.panelScope = { ...this.panelScope, dateRange: normalized };
    this.panelScopeState.next({ ...this.panelScope });
    if (autoResend && this.activeClarificationState.value === 'date_range') {
      void this.resendPending();
    }
  }

  /**
   * When the gateway answered on a message-named period that differed from the
   * sticky UI filter, keep Ask AI panel dates + report URL in sync.
   */
  private applyConversationDateRangeOverride(
    scope: ConversationScope | null | undefined,
  ): void {
    if (
      !scope ||
      scope.dateRangeSource !== 'message' ||
      !scope.dateRange?.start ||
      !scope.dateRange?.end
    ) {
      return;
    }
    const { start, end } = scope.dateRange;
    const current = this.panelScope.dateRange;
    if (current?.from !== start || current?.to !== end) {
      this.setPanelDateRange(start, end, false);
    }
    this.context.syncRouteDateRange(start, end);
  }

  setPanelTimezone(timezone: string): void {
    const tz = (timezone ?? '').trim();
    if (!tz) {
      return;
    }
    this.panelScope = { ...this.panelScope, timezone: tz };
    this.panelScopeState.next({ ...this.panelScope });
  }

  setAdvisorPersona(advisorPersona: AdvisorPersonaId): void {
    const next = {
      ...this.agentSettingsState.value,
      advisorPersona,
      tempoPersonality: tempoPersonalityForAdvisor(advisorPersona),
    };
    this.agentSettingsState.next(next);
    saveCopilotAgentSettings(next);
    this.segment.trackInAppEvents('Copilot', {
      name: 'Agent Settings Changed',
      location: COPILOT_EVENT_LOCATION,
      setting: 'advisorPersona',
      value: advisorPersona,
    });
  }

  /** @deprecated Prefer setAdvisorPersona. */
  setTempoPersonality(tempoPersonality: TempoPersonalityId): void {
    const advisor =
      tempoPersonality === 'executive_brief'
        ? 'alex'
        : tempoPersonality === 'analyst_detailed'
          ? 'sam'
          : 'maya';
    this.setAdvisorPersona(advisor as AdvisorPersonaId);
  }

  setDecisionEngineEnabled(decisionEngineEnabled: boolean): void {
    const next = { ...this.agentSettingsState.value, decisionEngineEnabled };
    this.agentSettingsState.next(next);
    saveCopilotAgentSettings(next);
    this.segment.trackInAppEvents('Copilot', {
      name: 'Agent Settings Changed',
      location: COPILOT_EVENT_LOCATION,
      setting: 'decisionEngineEnabled',
      value: decisionEngineEnabled,
    });
  }

  setAgentLongNarrativeEnabled(agentLongNarrativeEnabled: boolean): void {
    const next = { ...this.agentSettingsState.value, agentLongNarrativeEnabled };
    this.agentSettingsState.next(next);
    saveCopilotAgentSettings(next);
    this.segment.trackInAppEvents('Copilot', {
      name: 'Agent Settings Changed',
      location: COPILOT_EVENT_LOCATION,
      setting: 'agentLongNarrativeEnabled',
      value: agentLongNarrativeEnabled,
    });
  }

  private ensurePanelTimezone(): void {
    if (this.panelScope.timezone?.trim()) {
      return;
    }
    const tz = this.store.selectSnapshot(AuthState.userTimezone);
    if (tz?.trim()) {
      this.setPanelTimezone(tz);
    }
  }

  /** Seed Last 7 days when no panel/report range is set. */
  private ensureDefaultDateRangeForBrief(): void {
    if (panelScopeHasDateRange(this.panelScope)) {
      return;
    }
    const tz =
      this.panelScope.timezone?.trim() ||
      this.store.selectSnapshot(AuthState.userTimezone) ||
      Intl.DateTimeFormat().resolvedOptions().timeZone;
    const { from, to } = defaultLast7DaysRange(tz);
    this.setPanelDateRange(from, to, false);
  }

  /** Seed Last 7 days when live data is on and the user has not picked a range yet. */
  private ensureDefaultDateRangeForLiveData(): void {
    this.ensureDefaultDateRangeForBrief();
  }

  get composerWireForClarification(): string | null {
    return this.pendingComposerWire;
  }

  async resendPending(overrides?: CopilotSendOptions): Promise<void> {
    const pending = this.pendingResend;
    if (!pending || this.isSending.value) {
      return;
    }
    const merged = {
      ...pending,
      ...overrides,
      displayText: overrides?.displayText ?? pending.displayText,
      gatewayMessage: overrides?.gatewayMessage ?? pending.gatewayMessage,
    };
    const msgs = this.messages.value;
    if (msgs.length >= 2) {
      this.setActiveMessages(msgs.slice(0, -2));
    }
    this.clearClarificationState();
    await this.send(merged.displayText, merged);
  }

  private clearClarificationState(): void {
    this.activeClarificationState.next(null);
    this.pendingResend = null;
    this.pendingComposerWire = null;
  }

  /**
   * Re-send a prior user prompt. Truncates the thread from that turn onward so
   * the conversation does not accumulate duplicate user/assistant pairs.
   */
  async retryMessage(messageId: string): Promise<void> {
    if (this.isSending.value) {
      return;
    }

    const messages = this.messages.value;
    const index = messages.findIndex((m) => m.id === messageId);
    if (index < 0) {
      return;
    }

    const message = messages[index];
    if (message.pending || message.transient || !message.content.trim()) {
      return;
    }

    let userContent: string;
    let truncateAt: number;

    if (message.role === 'user') {
      userContent = message.content.trim();
      truncateAt = index;
    } else {
      let userIndex = index - 1;
      while (userIndex >= 0 && messages[userIndex].role !== 'user') {
        userIndex--;
      }
      const userMessage = messages[userIndex];
      if (!userMessage || userMessage.transient || !userMessage.content.trim()) {
        return;
      }
      userContent = userMessage.content.trim();
      truncateAt = userIndex;
    }

    this.segment.trackInAppEvents('Copilot', {
      name: 'Message Retried',
      location: COPILOT_EVENT_LOCATION,
      role: message.role,
    });

    this.setActiveMessages(messages.slice(0, truncateAt));
    const retryWire = messages[truncateAt]?.wire;
    if (retryWire) {
      const parsed = parseMentionWire(retryWire);
      await this.send(parsed.displayText, {
        wire: retryWire,
        displayText: parsed.displayText,
        gatewayMessage: parsed.gatewayMessage,
        selection: parsed.selection,
      });
      return;
    }
    await this.send(userContent);
  }

  async send(text: string, options?: CopilotSendOptions): Promise<void> {
    const content = (options?.displayText ?? text ?? '').trim();
    const gatewayMessage = (options?.gatewayMessage ?? text ?? '').trim();
    if (!content || this.isSending.value) {
      return;
    }

    const clarification = this.activeClarificationState.value;
    if (
      this.pendingResend &&
      (clarification === 'user' || clarification === 'team')
    ) {
      const hasUser = (options?.selection?.selectedUsers?.length ?? 0) > 0;
      const hasGroup = (options?.selection?.selectedGroups?.length ?? 0) > 0;
      const needsBoth =
        clarification === 'team' &&
        (this.pendingResend.selection?.selectedUsers?.length ?? 0) > 0;
      if (
        (needsBoth && hasUser && hasGroup) ||
        (!needsBoth && (hasUser || hasGroup))
      ) {
        await this.resendPending({
          ...options,
          displayText: content,
          gatewayMessage,
        });
        return;
      }
    }

    const now = new Date().toISOString();
    const userMessage: CopilotUiMessage = {
      id: this.id(),
      role: 'user',
      content,
      createdAt: now,
      wire: options?.wire,
    };
    const assistantMessage: CopilotUiMessage = {
      id: this.id(),
      role: 'assistant',
      content: '',
      createdAt: now,
      pending: true,
    };
    this.setActiveMessages([...this.messages.value, userMessage, assistantMessage]);
    this.isSending.next(true);
    this.lastSendPayload = {
      ...options,
      displayText: content,
      gatewayMessage,
      wire: options?.wire,
      selection: options?.selection,
    };
    this.segment.trackInAppEvents('Copilot', {
      name: 'Send Prompt Button Clicked',
      location: COPILOT_EVENT_LOCATION,
    });

    try {
      await this.sendViaAgentGateway(assistantMessage.id, gatewayMessage, options);
    } finally {
      this.isSending.next(false);
    }
  }

  private async sendViaAgentGateway(
    assistantId: string,
    gatewayMessage: string,
    options?: CopilotSendOptions,
  ): Promise<void> {
    if (this.mcpConfig.isConnected() || this.hasLiveDataDisclosureAck()) {
      await this.ensureLiveDataKey();
    }

    const baseContext = this.context.buildAgentGatewayContext(
      options?.selection,
      this.panelScope,
      this.agentSettingsState.value,
    );
    const overrides = options?.contextOverrides ?? {};
    const context: AgentGatewayContext = {
      ...baseContext,
      ...overrides,
      filters: {
        ...(baseContext.filters ?? {}),
        ...(overrides.filters ?? {}),
      },
      dateRange: overrides.dateRange ?? baseContext.dateRange,
      featureFlags: {
        ...(baseContext.featureFlags ?? {}),
        ...(overrides.featureFlags ?? {}),
      },
    };
    this.segment.trackInAppEvents('Copilot', {
      name: 'Agent Gateway Request Sent',
      location: COPILOT_EVENT_LOCATION,
      route: context.route,
    });

    const startedAt = Date.now();
    // Stream the answer so tokens render progressively. `conversationId` on the active tab
    // carries multi-turn memory: the gateway echoes it back on the first turn and we resend it.
    // The idempotency key makes transport-level retries replay the finished answer
    // instead of burning a second LLM turn (one key per logical send).
    const result = await this.agentGateway.streamMessage(
      {
        message: gatewayMessage,
        context,
        conversationId: this.activeGatewayConversationId(),
        idempotencyKey: this.id(),
      },
      {
        onConversationId: (id) => this.rememberConversationId(id),
        onToken: (_chunk, answerSoFar) =>
          this.patchAssistant(assistantId, {
            content: answerSoFar,
            pending: true,
          }),
        onStatus: (evt) => {
          const current = this.messages.value.find((m) => m.id === assistantId);
          const toolSteps = appendToolStepFromStatus(current?.toolSteps, evt);
          const label =
            (typeof evt.label === 'string' && evt.label.trim()) ||
            (evt.state === 'working'
              ? `${environment.assistantDisplayName} is working with Time Doctor Intelligence…`
              : null);
          if (toolSteps !== current?.toolSteps || label) {
            this.patchAssistant(assistantId, {
              pending: true,
              ...(toolSteps ? { toolSteps } : {}),
              ...(label ? { statusLabel: label } : {}),
            });
          }
        },
        onCard: (card) => {
          this.patchAssistant(assistantId, {
            pending: true,
            ...(card.reportLinks?.length ? { reportLinks: card.reportLinks } : {}),
            ...(card.shortFormat ? { shortFormat: card.shortFormat } : {}),
            ...(card.charts?.length ? { charts: card.charts } : {}),
            ...(card.tables?.length ? { tables: card.tables } : {}),
            ...(card.liveData ? { liveData: card.liveData } : {}),
            ...(card.conversationScope
              ? { conversationScope: card.conversationScope }
              : {}),
            ...(card.persona ? { persona: card.persona } : {}),
          });
        },
      },
    );
    const latencyMs = Date.now() - startedAt;

    if (result.status === 'success') {
      this.rememberConversationId(result.conversationId);
      // Navigation chips (route inside the app) come first, then re-askable
      // prompt suggestions. Nav routes are gateway-resolved, never model-invented.
      const navChips = this.toNavActions(result.navActions);
      const peopleChips = this.toPeopleActions(result.peopleActions ?? []);
      const coachingSuggestions = result.coachingSuggestions ?? [];
      // Coaching followUpActions are plain-text tips — never map them into
      // clickable prompt chips (legacy gateways still copy them into suggestedActions).
      const coachingTipKeys = new Set(
        coachingSuggestions.flatMap((c) =>
          (c.followUpActions ?? []).map((a) => this.navigationLabelKey(a)),
        ),
      );
      const suggested = result.suggestedActions ?? [];
      // Soft guidance ("Not what you meant?…") stays visible as static copy —
      // never a clickable chip that re-sends itself as a prompt.
      const softHint =
        suggested.find((s) => CopilotFacadeService.softHintPatterns.some((p) => p.test(s))) ??
        null;
      const promptSuggestions = suggested.filter(
        (s) =>
          !coachingTipKeys.has(this.navigationLabelKey(s)) &&
          !CopilotFacadeService.softHintPatterns.some((p) => p.test(s)),
      );
      const mergedActions = this.mergeActions(
        [...peopleChips, ...navChips],
        promptSuggestions,
      );
      const actions = result.shortFormat ? [] : mergedActions;
      const helpfulLinks = result.gatewayError ? [] : (result.helpfulLinks ?? []);
      const charts = result.charts ?? [];
      let tables = result.tables ?? [];
      this.segment.trackInAppEvents('Copilot', {
        name: 'Agent Gateway Response Received',
        location: COPILOT_EVENT_LOCATION,
        latencyMs,
        tool: result.tool,
        unsupported: result.unsupported,
        suggestedActionCount: actions.length,
        navActionCount: navChips.length,
        helpfulLinkCount: helpfulLinks.length,
        chartCount: charts.length,
        totalTokens: result.usage?.totalTokens ?? 0,
      });
      const caveats = result.caveats ?? [];
      // The scope provenance line and the truncation disclosure both arrive as
      // trailing paragraphs of the answer; strip them and render structured
      // chips/notes instead (meta.scopeEcho / result_truncated caveat).
      let answer = this.stripStructuredTailParagraphs(
        (result.answer ?? '').trim(),
        result.scopeEcho ?? null,
        caveats.includes('result_truncated'),
      );
      // Client-only: when the gateway did not attach TableSpec, lift GFM/TSV
      // grids out of the prose so Copilot can render the table presenter.
      if (!tables.length && answer) {
        const extracted = extractInlineTablesFromAnswer(answer);
        if (extracted.tables.length) {
          tables = extracted.tables;
          answer = extracted.content;
        }
      } else if (tables.length && answer) {
        // Structured table already attached — drop the duplicate markdown grid
        // the model often re-quotes into the answer.
        answer = extractInlineTablesFromAnswer(answer).content;
      }
      this.patchAssistant(assistantId, {
        content: answer.length > 0 ? answer : this.translate.instant('copilot.error.emptyReply'),
        actions,
        helpfulLinks,
        coachingSuggestions,
        charts: charts.length ? charts : undefined,
        tables: tables.length ? tables : undefined,
        usage: result.usage,
        model: result.model,
        clarification: result.clarification ?? null,
        decision: result.decision ?? undefined,
        requestId: result.requestId,
        tool: result.tool,
        persona: result.persona ?? undefined,
        liveData: result.liveData ?? undefined,
        conversationScope: result.conversationScope ?? undefined,
        peopleActions: result.peopleActions?.length ? result.peopleActions : undefined,
        usageChips: result.usageChips?.length ? result.usageChips : undefined,
        reportLinks: result.reportLinks?.length ? result.reportLinks : undefined,
        shortFormat: result.shortFormat ?? undefined,
        recommendationCard: result.recommendationCard ?? undefined,
        toolChecks: result.toolChecks?.length ? result.toolChecks : undefined,
        toolSteps: result.toolSteps?.length
          ? result.toolSteps
          : markToolStepsDone(
              this.messages.value.find((m) => m.id === assistantId)?.toolSteps,
            ),
        answerSource: result.answerSource ?? null,
        retryable: result.retryable ?? null,
        scopeEcho: result.scopeEcho ?? null,
        caveats: caveats.length ? caveats : undefined,
        softHint: softHint || undefined,
        statusLabel: null,
        error: !!result.gatewayError,
        // Surface the normalized tool output only in dev builds (debugging aid; never in prod).
        sourceData: isDevMode() ? result.sourceData : undefined,
        pending: false,
      });
      this.applyConversationDateRangeOverride(result.conversationScope);
      if (result.clarification || result.gatewayError) {
        this.activeClarificationState.next(result.clarification ?? null);
        if (this.lastSendPayload) {
          this.pendingResend = { ...this.lastSendPayload };
          this.pendingComposerWire = this.lastSendPayload.wire ?? null;
        }
        if (result.clarification) {
          this.segment.trackInAppEvents('Copilot', {
            name: 'Agent Gateway Clarification',
            location: COPILOT_EVENT_LOCATION,
            clarification: result.clarification,
          });
        }
      } else {
        this.clearClarificationState();
      }
      return;
    }

    this.segment.trackInAppEvents('Copilot', {
      name: 'Agent Gateway Request Failed',
      location: COPILOT_EVENT_LOCATION,
      latencyMs,
      errorKind: result.errorKind,
    });
    // Prefer the gateway envelope's human message; surface the requestId so
    // support can find the exact request in logs.
    const errorText =
      result.message?.trim() || this.gatewayErrorMessage(result.errorKind);
    this.patchAssistant(assistantId, {
      content: result.requestId
        ? `${errorText}\n\n_Ref: ${result.requestId}_`
        : errorText,
      error: true,
      // False → permanent (quota / not configured): hide retry affordances.
      retryable: result.retryable ?? null,
      pending: false,
    });
  }

  /**
   * Remove the gateway's structured trailing paragraphs from an answer:
   * the scope provenance line (rendered as a chip from meta.scopeEcho) and the
   * truncation disclosure (rendered as a note from the result_truncated caveat).
   */
  private stripStructuredTailParagraphs(
    answer: string,
    scopeEcho: string | null,
    truncated: boolean,
  ): string {
    let paragraphs = answer.split(/\n{2,}/);
    const isStructuredTail = (raw: string): boolean => {
      const text = raw.trim().replace(/^[_*]+/, '').replace(/[_*]+$/, '').trim();
      if (scopeEcho && text.startsWith('Based on ')) {
        return true;
      }
      return truncated && text.includes('totals above cover only them');
    };
    // Both lines sit at the tail (scope echo last, disclosure just before it).
    let guard = 2;
    while (guard-- > 0 && paragraphs.length > 1 && isStructuredTail(paragraphs[paragraphs.length - 1])) {
      paragraphs = paragraphs.slice(0, -1);
    }
    return paragraphs.join('\n\n').trim();
  }

  /** Map the gateway's plain-string suggestions into re-askable prompt chips. */
  private toActions(suggestions: string[]): CopilotAction[] {
    return suggestions
      .map((s) => (s ?? '').trim())
      .filter((s) => s.length > 0)
      .map((s) => this.mapGatewayAction(s))
      .filter((action): action is CopilotAction => action != null);
  }

  /** Never send meta-instruction strings or gateway control tokens as chat prompts. */
  private mapGatewayAction(raw: string): CopilotAction | null {
    if (raw === GATEWAY_CHIP_OPEN_SCOPE) {
      return { label: 'Choose date range', type: 'open_scope' };
    }
    if (raw === GATEWAY_CHIP_RETRY_LAST) {
      return { label: 'Try again', type: 'retry_last' };
    }
    const dateChip = mapDateClarificationChip(raw);
    if (dateChip) {
      return { ...dateChip } as CopilotAction;
    }
    if (CopilotFacadeService.metaPromptPatterns.some((pattern) => pattern.test(raw))) {
      return null;
    }
    return { label: raw, prompt: raw };
  }

  private static readonly metaPromptPatterns = [
    /^rephrase the question/i,
    /^try rephrasing/i,
    /^ask again with a shorter date range$/i,
    /^try a different date range$/i,
    /^check that the selected team has tracked time$/i,
    /^check with an admin/i,
    /^open the timeline for @/i,
    /^compare the same (people|period)/i,
    /^review timeout after settings/i,
    /^@-select a (user|group)$/i,
    /^include a name like /i,
    /^say "members of /i,
    /^not what you meant\?/i,
  ];

  /** Tips shown as static copy (never clickable prompt chips). */
  private static readonly softHintPatterns = [
    /^not what you meant\?/i,
  ];

  /** Map Dig-into peopleActions into primary prompt chips. */
  private toPeopleActions(
    people: Array<{
      label: string;
      prompt: string;
      nav?: { route: string, params?: Record<string, string>, icon?: string };
    }>,
  ): CopilotAction[] {
    return (people ?? [])
      .filter((p) => p && typeof p.label === 'string' && typeof p.prompt === 'string')
      .map((p) => ({
        type: 'dig_into',
        label: p.label,
        prompt: p.prompt,
        ...(p.nav?.route
          ? {
              // Prefer prompt follow-up over nav; keep icon for Dig-into styling.
              icon: p.nav.icon || 'person_search',
            }
          : { icon: 'person_search' }),
      }));
  }

  /** Map gateway nav actions into navigate chips (route + optional params/icon). */
  private toNavActions(navActions: NavAction[]): CopilotAction[] {
    return (navActions ?? [])
      .filter((n) => n && typeof n.label === 'string' && typeof n.route === 'string')
      .map((n) => ({
        label: n.label,
        route: n.route,
        params: n.params,
        icon: n.icon,
      }));
  }

  /**
   * Nav chips first, then prompt suggestions — dropping duplicates of nav labels
   * (the model often repeats "Go to the Invite page" when the gateway already
   * attached that route chip).
   */
  private mergeActions(navChips: CopilotAction[], suggestions: string[]): CopilotAction[] {
    const navKeys = new Set(navChips.map((n) => this.navigationLabelKey(n.label)));
    const seen = new Set<string>();
    const prompts = this.toActions(suggestions)
      .map((action) => this.sanitizeCopilotAction(action))
      .filter((action) => {
      const key = this.navigationLabelKey(action.label);
      if (navKeys.has(key) || seen.has(key)) {
        return false;
      }
      seen.add(key);
      return true;
    });
    return [...navChips.map((a) => this.sanitizeCopilotAction(a)), ...prompts];
  }

  private navigationLabelKey(label: string): string {
    return label
      .trim()
      .toLowerCase()
      .replace(/^(go to (the )?|open (the )?|visit (the )?)/, '')
      .replace(/\bidle[\s-]*time\s*report\b/, 'activity summary report');
  }

  /** Idle time lives on Activity Summary — drop legacy Idle Time report chips. */
  private sanitizeCopilotAction(action: CopilotAction): CopilotAction {
    const route = (action.route ?? '').toLowerCase();
    if (route === '/idle-time' || route === '/reports/idle-time') {
      return {
        ...action,
        label: 'Open the Activity Summary report',
        route: '/activity-summary',
      };
    }
    if (/\bidle[\s-]*time\s*report\b/i.test(action.label ?? '')) {
      return {
        ...action,
        label: 'Open the Activity Summary report',
        route: action.route ?? '/activity-summary',
      };
    }
    // Screenshot activation lives on Manage Users — gateway sometimes points at Screencasts instead.
    if (
      /\bscreenshots?\s*settings\b/i.test(action.label ?? '')
      && (route === '/screencasts' || route === '/reports/screencasts')
    ) {
      return {
        ...action,
        route: '/manage-users',
      };
    }
    return action;
  }

  private gatewayErrorMessage(kind: AgentGatewayErrorKind): string {
    const key = `copilot.error.${kind}`;
    const message = this.translate.instant(key);
    // Fall back to the generic error if a specific translation is missing.
    return message === key ? this.translate.instant('copilot.error.generic') : message;
  }

  async resendLast(): Promise<void> {
    const last = this.lastSendPayload;
    if (!last || this.isSending.value) {
      return;
    }
    const msgs = this.messages.value;
    if (msgs.length >= 2) {
      this.setActiveMessages(msgs.slice(0, -2));
    }
    await this.send(last.displayText, last);
  }

  /** Track a click on an Agent Gateway suggested action. */
  onSuggestedActionClicked(action: CopilotAction, message?: CopilotUiMessage): void {
    this.segment.trackInAppEvents('Copilot', {
      name: 'Agent Gateway Suggested Action Clicked',
      location: COPILOT_EVENT_LOCATION,
      hasRoute: !!action.route,
    });
    if (message?.requestId && this.activeGatewayConversationId()) {
      void this.agentGateway.submitFeedback({
        rating: 'helpful',
        conversationId: this.activeGatewayConversationId(),
        messageId: message.id,
        requestId: message.requestId,
        recommendationType: message.decision?.recommendationType,
        clickedAction: action.label,
      });
    }
  }

  /**
   * Find the user question that produced a given assistant answer, so feedback
   * can be tracked together with the prompt that triggered it. Walks back to the
   * nearest preceding user message.
   */
  private findQuestionForMessage(message: CopilotUiMessage): string | undefined {
    const messages = this.messages.value;
    const index = messages.findIndex((m) => m.id === message.id);
    if (index < 0) {
      return undefined;
    }
    for (let i = index - 1; i >= 0; i--) {
      const content = messages[i].content.trim();
      if (messages[i].role === 'user' && content) {
        return content;
      }
    }
    return undefined;
  }

  /** Submit helpful / not helpful feedback for an assistant answer. */
  async submitMessageFeedback(
    message: CopilotUiMessage,
    rating: 'helpful' | 'not_helpful',
  ): Promise<void> {
    const conversationId = this.activeGatewayConversationId();
    if (!message.requestId || !conversationId) {
      return;
    }
    const question = this.findQuestionForMessage(message);
    const ok = await this.agentGateway.submitFeedback({
      rating,
      conversationId,
      messageId: message.id,
      requestId: message.requestId,
      recommendationType: message.decision?.recommendationType,
    });
    if (ok) {
      this.patchAssistant(message.id, { feedbackRating: rating });
      this.segment.trackInAppEvents('Copilot', {
        name: 'Agent Gateway Feedback Submitted',
        location: COPILOT_EVENT_LOCATION,
        rating,
        recommendationType: message.decision?.recommendationType,
        question,
      });
    }
  }

  /** Track a click on a curated "Learn more" Help Center link. */
  onHelpLinkClicked(link: HelpLink): void {
    this.segment.trackInAppEvents('Copilot', {
      name: 'Agent Gateway Helpful Link Clicked',
      location: COPILOT_EVENT_LOCATION,
      title: link.title,
    });
  }

  /**
   * Persist the gateway's conversation id on the active tab so the next turn resends it for
   * multi-turn memory. No-op if unchanged or empty.
   */
  private rememberConversationId(conversationId: string | undefined): void {
    if (!conversationId) {
      return;
    }
    const activeId = this.activeConversationIdState.value;
    const active = this.activeConversation();
    if (!active || active.conversationId === conversationId) {
      return;
    }
    this.conversationsState.next(
      this.conversationsState.value.map((c) =>
        c.id === activeId ? { ...c, conversationId } : c,
      ),
    );
    this.schedulePersist();
  }

  /** Dismiss the recommendation card on Skip (client-only; no gateway write). */
  clearRecommendationCard(messageId: string): void {
    this.patchAssistant(messageId, { recommendationCard: undefined });
  }

  private patchAssistant(id: string, patch: Partial<CopilotUiMessage>): void {
    this.setActiveMessages(
      this.messages.value.map((m) => (m.id === id ? { ...m, ...patch } : m)),
    );
  }

  /**
   * Append a UI-only assistant notice (used for live-data connect/disconnect feedback). Marked
   * `transient` so it is shown in the panel but never resent to the agent as conversation history.
   */
  private pushAssistantInfo(content: string, error = false): void {
    const message: CopilotUiMessage = {
      id: this.id(),
      role: 'assistant',
      content,
      createdAt: new Date().toISOString(),
      error,
      transient: true,
    };
    this.setActiveMessages([...this.messages.value, message]);
  }

  /**
   * Client-side @mention confirmation (STAFF-145672). Keeps the prompt in the composer
   * and shows a clarification notice without calling the gateway — so we never answer
   * for the wrong person/group. Composer already opens the picker for the unresolved label.
   */
  requestMentionConfirmation(opts: {
    reason: 'ambiguous' | 'unresolved';
    label: string;
    wire: string;
    clarification: 'user' | 'team';
  }): void {
    const key =
      opts.reason === 'ambiguous'
        ? 'copilot.clarification.ambiguous'
        : 'copilot.clarification.unresolved';
    const content = this.translate.instant(key, { label: opts.label });
    this.pushAssistantInfo(content);
    this.pendingComposerWire = opts.wire;
    this.segment.trackInAppEvents('Copilot', {
      name: 'Mention Clarification Requested',
      location: COPILOT_EVENT_LOCATION,
      reason: opts.reason,
      clarification: opts.clarification,
    });
  }

  private id(): string {
    return `${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;
  }
}
